import { Theme, useTheme } from '@mui/material/styles';
import { isCPPServiceLevel } from '@provision/service-level-utils';
import { useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { SelectMenuOption } from 'src/components/SelectMenu';
import {
  formatServiceLevelWithCpp,
  getServiceLevelColors,
  ServiceLevelOption,
} from 'src/graphql';

export type ServiceLevelFormValue = { id: string; label: string };

function getServiceLevelIcon(serviceLevel: string, theme: Theme) {
  const styles = getServiceLevelColors(serviceLevel, theme);
  const isCpp = isCPPServiceLevel(serviceLevel);

  return (
    <DynamicIcon
      color={styles.color}
      library="lucide"
      name={isCpp ? 'circle-dot' : 'circle'}
      sx={{
        '& svg': {
          fill: !isCpp && styles.filled ? 'currentColor' : 'none',
        },
      }}
    />
  );
}

export function useServiceLevelSelectMenuOptions({
  serviceLevelOptions,
  currentServiceLevel,
  cppExpDate,
}: {
  serviceLevelOptions: ServiceLevelOption[];
  currentServiceLevel: ServiceLevelFormValue | null;
  cppExpDate?: string | null;
}) {
  const theme = useTheme();

  return useMemo<SelectMenuOption[]>(() => {
    const options = serviceLevelOptions.map((option) => {
      const title = formatServiceLevelWithCpp(option.label, cppExpDate);

      return {
        value: option.id,
        title,
        icon: getServiceLevelIcon(title, theme),
      };
    });

    if (
      currentServiceLevel &&
      !options.some(
        (option) =>
          option.value === currentServiceLevel.id ||
          option.title === currentServiceLevel.label,
      )
    ) {
      options.unshift({
        value: currentServiceLevel.id,
        title: currentServiceLevel.label,
        icon: getServiceLevelIcon(currentServiceLevel.label, theme),
      });
    }

    return options;
  }, [cppExpDate, currentServiceLevel, serviceLevelOptions, theme]);
}
